<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-indigo-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-indigo-600 tracking-widest">Administración</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Contratos de Servicios Públicos
          <span class="px-2 sm:px-3 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 text-xs sm:text-sm font-black rounded-lg border border-indigo-200 dark:border-indigo-800">
            {{ contratos.length }}
          </span>
        </h1>
      </div>
      <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap justify-end">
        <button @click="cargarTodo" :disabled="loading" class="px-3 sm:px-4 py-2 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-sm flex items-center gap-1 sm:gap-2">
          <Icon icon="mdi:refresh" :class="{ 'animate-spin': loading }" class="text-base" />
          <span class="hidden sm:inline">Actualizar</span>
        </button>
        <button @click="openCreateModal" class="px-3 sm:px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-1 sm:gap-2">
          <Icon icon="mdi:file-document-plus" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Nuevo Contrato</span>
          <span class="sm:hidden">Nuevo</span>
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-5">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1">Buscar</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg sm:text-2xl group-focus-within:text-indigo-500 transition-colors" />
            <input v-model="searchQuery" type="text" placeholder="Alias, N° contrato, medidor, proveedor..." class="w-full pl-10 sm:pl-14 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 rounded-xl transition-all duration-300 outline-none dark:text-white font-bold text-sm sm:text-base" />
          </div>
        </div>
        <div class="sm:col-span-4">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1">Servicio</label>
          <select v-model="filterServicio" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base">
            <option value="all">TODOS</option>
            <option v-for="s in servicios" :key="s.idServicioPublico" :value="s.idServicioPublico">{{ s.nombre }}</option>
          </select>
        </div>
        <div class="sm:col-span-3">
          <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2 ml-1">Estado</label>
          <select v-model="filterEstado" class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-xl font-black text-gray-800 dark:text-white outline-none transition-all appearance-none cursor-pointer text-sm sm:text-base">
            <option value="all">TODOS</option>
            <option value="activo">ACTIVOS</option>
            <option value="inactivo">INACTIVOS</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Tabla / Tarjetas -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <!-- Loading -->
      <div v-if="loading" class="p-10 text-center">
        <Icon icon="mdi:loading" class="animate-spin text-4xl text-indigo-500 mx-auto" />
        <p class="text-xs font-black text-gray-400 mt-3 tracking-widest">CARGANDO...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="filteredContratos.length === 0" class="p-16 text-center">
        <div class="flex flex-col items-center opacity-30 select-none">
          <Icon icon="mdi:file-document-outline" class="text-7xl mb-3" />
          <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay contratos</h4>
          <p class="text-sm text-gray-300 mt-1">Registra tu primer contrato</p>
        </div>
      </div>

      <!-- =============================== -->
      <!-- DESKTOP: TABLA -->
      <!-- =============================== -->
      <div v-else class="hidden lg:block overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Contrato</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Servicio</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Proveedor</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Período</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-for="c in paginatedContratos" :key="c.idContratoServicio" class="hover:bg-indigo-50/30 dark:hover:bg-gray-700/30 transition-all">
              <td class="px-5 py-4">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm"
                       :style="{ backgroundColor: getColorServicio(c) + '20' }">
                    <Icon :icon="getIconoServicio(c)" class="text-lg"
                          :style="{ color: getColorServicio(c) }" />
                  </div>
                  <div>
                    <p class="text-sm font-black text-gray-900 dark:text-white">{{ c.alias || 'Sin alias' }}</p>
                    <p class="text-[10px] text-gray-500 dark:text-gray-400">N°: {{ c.numero_contrato || 'N/A' }}</p>
                  </div>
                </div>
              </td>
              <td class="px-5 py-4">
                <p class="text-xs font-black text-indigo-600 dark:text-indigo-400">{{ c.nombreServicioPublico }}</p>
                <p class="text-[10px] text-gray-500 uppercase">{{ c.tipoServicio }}</p>
              </td>
              <td class="px-5 py-4">
                <p class="text-xs font-bold text-gray-700 dark:text-gray-300">{{ c.proveedor || '—' }}</p>
                <p v-if="c.numero_medidor" class="text-[10px] text-gray-500">Medidor: {{ c.numero_medidor }}</p>
              </td>
              <td class="px-5 py-4">
                <span class="text-xs font-black text-gray-700 dark:text-gray-300 uppercase">{{ c.periodicidad }}</span>
                <p v-if="c.dia_vencimiento" class="text-[10px] text-gray-500">Vence día {{ c.dia_vencimiento }}</p>
              </td>
              <td class="px-5 py-4 text-center">
                <span :class="[
                  'px-3 py-1 inline-flex text-[10px] font-black uppercase tracking-widest rounded-lg border',
                  c.estado === 'activo'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
                    : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
                ]">{{ c.estado }}</span>
              </td>
              <td class="px-5 py-4 text-right">
                <div class="flex items-center justify-end gap-1">
                  <button @click="openEditModal(c)" class="p-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-600 hover:text-white transition-all" title="Editar">
                    <Icon icon="mdi:pencil" class="text-base" />
                  </button>
                  <button
                    @click="abrirModalToggleStatus(c)"
                    :class="[
                      'p-1.5 rounded-lg transition-all',
                      c.estado === 'activo'
                        ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white'
                        : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white'
                    ]"
                    :title="c.estado === 'activo' ? 'Desactivar' : 'Activar'">
                    <Icon :icon="c.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-base" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- =============================== -->
      <!-- MÓVIL / TABLET: TARJETAS -->
      <!-- =============================== -->
      <div v-if="!loading && filteredContratos.length > 0" class="lg:hidden p-2 sm:p-3 space-y-3">
        <div v-for="c in paginatedContratos" :key="'card-' + c.idContratoServicio"
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-all">

          <!-- Header: Icono + Alias + Estado -->
          <div class="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-3 min-w-0 flex-1">
              <div class="w-12 h-12 flex-shrink-0 rounded-xl flex items-center justify-center shadow-sm"
                   :style="{ backgroundColor: getColorServicio(c) + '20' }">
                <Icon :icon="getIconoServicio(c)" class="text-xl"
                      :style="{ color: getColorServicio(c) }" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-black text-gray-900 dark:text-white truncate">
                  {{ c.alias || 'Sin alias' }}
                </p>
                <p class="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                  {{ c.nombreServicioPublico }} • {{ c.tipoServicio }}
                </p>
              </div>
            </div>
            <span :class="[
              'px-2.5 py-1 text-[9px] font-black uppercase tracking-widest rounded-lg border flex-shrink-0',
              c.estado === 'activo'
                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800'
                : 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:border-rose-800'
            ]">{{ c.estado }}</span>
          </div>

          <!-- Info grid -->
          <div class="grid grid-cols-2 gap-3 mb-3">
            <div>
              <p class="text-[9px] font-black text-gray-400 tracking-widest uppercase">N° Contrato</p>
              <p class="text-xs font-bold text-gray-800 dark:text-white truncate">
                {{ c.numero_contrato || '—' }}
              </p>
            </div>
            <div>
              <p class="text-[9px] font-black text-gray-400 tracking-widest uppercase">N° Medidor</p>
              <p class="text-xs font-bold text-gray-800 dark:text-white truncate">
                {{ c.numero_medidor || '—' }}
              </p>
            </div>
            <div>
              <p class="text-[9px] font-black text-gray-400 tracking-widest uppercase">Proveedor</p>
              <p class="text-xs font-bold text-gray-800 dark:text-white truncate">
                {{ c.proveedor || '—' }}
              </p>
            </div>
            <div>
              <p class="text-[9px] font-black text-gray-400 tracking-widest uppercase">Período</p>
              <p class="text-xs font-bold text-gray-800 dark:text-white uppercase truncate">
                {{ c.periodicidad }}
                <span v-if="c.dia_vencimiento" class="text-[10px] text-gray-500 normal-case font-medium">
                  (día {{ c.dia_vencimiento }})
                </span>
              </p>
            </div>
          </div>

          <!-- Monto estimado -->
          <div v-if="c.monto_estimado > 0" class="mb-3 p-2.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl flex items-center justify-between">
            <span class="text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest flex items-center gap-1">
              <Icon icon="mdi:currency-usd" class="text-sm" />
              Monto estimado
            </span>
            <span class="text-sm font-black text-indigo-700 dark:text-indigo-300">
              ${{ Number(c.monto_estimado).toFixed(2) }}
            </span>
          </div>

          <!-- Acciones -->
          <div class="flex gap-2 pt-3 border-t border-gray-100 dark:border-gray-700">
            <button @click="openEditModal(c)"
                    class="flex-1 py-2.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-xl font-black text-[10px] tracking-widest hover:bg-amber-500 hover:text-white transition-all flex items-center justify-center gap-1.5">
              <Icon icon="mdi:pencil" class="text-sm" />
              Editar
            </button>
            <button @click="abrirModalToggleStatus(c)"
                    :class="[
                      'flex-1 py-2.5 rounded-xl font-black text-[10px] tracking-widest transition-all flex items-center justify-center gap-1.5',
                      c.estado === 'activo'
                        ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-500 hover:text-white'
                        : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500 hover:text-white'
                    ]">
              <Icon :icon="c.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye'" class="text-sm" />
              {{ c.estado === 'activo' ? 'Desactivar' : 'Activar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- PAGINACIÓN -->
    <!-- ============================================ -->
    <div v-if="filteredContratos.length > 0 && !loading"
         class="mt-4 sm:mt-5 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 px-3 sm:px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">

      <div class="flex items-center gap-3 flex-wrap justify-center sm:justify-start">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          Mostrando <span class="text-indigo-600">{{ startItem }}</span> - <span class="text-indigo-600">{{ endItem }}</span>
          de <span class="text-indigo-600">{{ filteredContratos.length }}</span>
        </div>

        <div class="flex items-center gap-2">
          <span class="text-[10px] font-black text-gray-400 uppercase tracking-widest hidden sm:inline">Mostrar:</span>
          <select v-model.number="itemsPerPage" @change="currentPage = 1"
                  class="px-2 py-1 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-indigo-500 rounded-lg text-[11px] font-black text-gray-700 dark:text-white outline-none cursor-pointer">
            <option :value="5">5</option>
            <option :value="10">10</option>
            <option :value="25">25</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </div>
      </div>

      <div class="flex items-center gap-1 flex-wrap justify-center">
        <button @click="currentPage = 1" :disabled="currentPage === 1"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === 1
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']"
                title="Primera página">
          <Icon icon="mdi:page-first" class="text-base sm:text-lg" />
        </button>

        <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === 1
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']"
                title="Anterior">
          <Icon icon="mdi:chevron-left" class="text-base sm:text-lg" />
        </button>

        <div class="hidden sm:flex items-center gap-0.5 mx-1">
          <button v-for="page in visiblePages" :key="page"
                  @click="currentPage = page"
                  :class="['min-w-[34px] h-9 rounded-lg text-[11px] font-black transition-all flex items-center justify-center',
                    currentPage === page
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-md shadow-indigo-500/30'
                      : 'text-gray-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800']">
            {{ page }}
          </button>
        </div>

        <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2 whitespace-nowrap">
          Pág. {{ currentPage }} / {{ totalPages }}
        </span>

        <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === totalPages
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']"
                title="Siguiente">
          <Icon icon="mdi:chevron-right" class="text-base sm:text-lg" />
        </button>

        <button @click="currentPage = totalPages" :disabled="currentPage === totalPages"
                :class="['p-1.5 rounded-lg border transition-all',
                  currentPage === totalPages
                    ? 'opacity-30 cursor-not-allowed border-gray-200 dark:border-gray-700'
                    : 'hover:bg-indigo-50 dark:hover:bg-indigo-900/20 border-gray-200 dark:border-gray-600 shadow-sm text-gray-600 dark:text-gray-300']"
                title="Última página">
          <Icon icon="mdi:page-last" class="text-base sm:text-lg" />
        </button>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL FORM -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]" @click.self="closeModal">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden animate-zoomIn">
          <div class="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center gap-3 sticky top-0 z-10">
            <div class="p-2 bg-white/20 rounded-xl">
              <Icon :icon="editing ? 'mdi:pencil-box' : 'mdi:file-document-plus'" class="text-white text-xl" />
            </div>
            <div>
              <h3 class="text-sm font-black text-white">{{ editing ? 'Editar Contrato' : 'Nuevo Contrato' }}</h3>
              <p class="text-[10px] text-white/70 font-bold">Datos del contrato con el proveedor</p>
            </div>
            <button @click="closeModal" class="ml-auto p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <Icon icon="mdi:close" class="text-white text-lg" />
            </button>
          </div>
          <div class="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <form @submit.prevent="saveContrato" class="space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Servicio Público *</label>
                  <select v-model.number="form.idServicioPublico" required class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none cursor-pointer">
                    <option :value="0" disabled>Seleccione un servicio</option>
                    <option v-for="s in servicios" :key="s.idServicioPublico" :value="s.idServicioPublico">{{ s.nombre }}</option>
                  </select>
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Alias</label>
                  <input v-model="form.alias" type="text" maxlength="100" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none" placeholder="Ej: Luz Local Principal" />
                </div>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">N° Contrato / Cuenta</label>
                  <input v-model="form.numero_contrato" type="text" maxlength="100" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none" />
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">N° Medidor</label>
                  <input v-model="form.numero_medidor" type="text" maxlength="100" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none" />
                </div>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Proveedor / Empresa</label>
                <input v-model="form.proveedor" type="text" maxlength="200" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none" placeholder="Ej: CORPOELEC, Hidrocapital..." />
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Dirección del Servicio</label>
                <textarea v-model="form.direccion_servicio" rows="2" maxlength="200" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-medium text-gray-800 dark:text-white outline-none resize-none"></textarea>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Monto Estimado USD</label>
                  <input v-model.number="form.monto_estimado" type="number" step="0.01" min="0" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-bold text-gray-800 dark:text-white outline-none" />
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Periodicidad</label>
                  <select v-model="form.periodicidad" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-black text-gray-800 dark:text-white outline-none cursor-pointer">
                    <option v-for="p in PERIODICIDADES" :key="p.value" :value="p.value">{{ p.label }}</option>
                  </select>
                </div>
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Día Vencimiento</label>
                  <input v-model.number="form.dia_vencimiento" type="number" min="1" max="31" class="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-indigo-500 rounded-xl text-xs font-bold text-gray-800 dark:text-white outline-none" placeholder="1-31" />
                </div>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                <label class="block text-[10px] font-black text-gray-400 tracking-widest mb-2">Estado</label>
                <div class="flex gap-4">
                  <label class="flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="radio" v-model="form.estado" value="activo" class="w-4 h-4 text-indigo-600" /> Activo
                  </label>
                  <label class="flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-gray-300 cursor-pointer">
                    <input type="radio" v-model="form.estado" value="inactivo" class="w-4 h-4 text-indigo-600" /> Inactivo
                  </label>
                </div>
              </div>

              <div class="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="closeModal" class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest">Cancelar</button>
                <button type="submit" :disabled="saving" class="flex-[2] py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50">
                  <Icon :icon="saving ? 'mdi:loading' : 'mdi:content-save'" :class="{ 'animate-spin': saving }" />
                  {{ saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear Contrato') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ============================================ -->
    <!-- MODAL CONFIRMAR TOGGLE STATUS -->
    <!-- ============================================ -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[10002]" @click.self="cancelarConfirmacion">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn">
          <div :class="[
            'px-6 py-4',
            confirmTarget?.estado === 'activo'
              ? 'bg-gradient-to-r from-red-600 to-orange-600'
              : 'bg-gradient-to-r from-emerald-600 to-teal-600'
          ]">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-white/20 rounded-xl">
                <Icon :icon="confirmTarget?.estado === 'activo' ? 'mdi:close-circle' : 'mdi:check-circle'" class="text-white text-xl" />
              </div>
              <div>
                <h3 class="text-sm font-black text-white">
                  {{ confirmTarget?.estado === 'activo' ? 'Desactivar Contrato' : 'Activar Contrato' }}
                </h3>
                <p class="text-[10px] text-white/80">Cambiar estado del contrato</p>
              </div>
            </div>
          </div>

          <div class="p-6">
            <p class="text-sm text-gray-700 dark:text-gray-300">
              ¿{{ confirmTarget?.estado === 'activo' ? 'Desactivar' : 'Activar' }} el contrato
              <strong>"{{ confirmTarget?.alias || confirmTarget?.numero_contrato || 'Sin alias' }}"</strong>?
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Pasará de <strong>"{{ confirmTarget?.estado }}"</strong> a
              <strong>"{{ confirmTarget?.estado === 'activo' ? 'inactivo' : 'activo' }}"</strong>.
            </p>
          </div>

          <div class="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex gap-3">
            <button @click="cancelarConfirmacion" class="flex-1 py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-xs tracking-widest">Cancelar</button>
            <button @click="ejecutarToggleStatus" :disabled="confirmLoading"
                    :class="[
                      'flex-[2] py-3 text-white rounded-xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50 transition-all',
                      confirmTarget?.estado === 'activo'
                        ? 'bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700'
                        : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700'
                    ]">
              <Icon :icon="confirmLoading ? 'mdi:loading' : (confirmTarget?.estado === 'activo' ? 'mdi:eye-off' : 'mdi:eye')" :class="{ 'animate-spin': confirmLoading }" />
              {{ confirmLoading ? 'Procesando...' : (confirmTarget?.estado === 'activo' ? 'Desactivar' : 'Activar') }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';
import { useToast } from 'vue-toastification';

import {
  obtenerContratos,
  crearContrato,
  actualizarContrato,
  PERIODICIDADES
} from '@/api/contratoServicio.js';

import {
  obtenerServiciosPublicos,
  getColorServicio,
  getIconoServicio
} from '@/api/servicioPublico.js';

export default {
  name: 'ContratoPublico',
  components: { Icon },
  data() {
    return {
      contratos: [],
      servicios: [],
      loading: false,
      saving: false,
      showModal: false,
      editing: null,
      searchQuery: '',
      filterServicio: 'all',
      filterEstado: 'all',
      PERIODICIDADES,
      form: {
        idServicioPublico: 0,
        alias: '',
        numero_contrato: '',
        numero_medidor: '',
        proveedor: '',
        direccion_servicio: '',
        monto_estimado: 0,
        periodicidad: 'mensual',
        dia_vencimiento: 15,
        estado: 'activo'
      },
      currentPage: 1,
      itemsPerPage: 10,
      showConfirmModal: false,
      confirmTarget: null,
      confirmLoading: false
    };
  },
  computed: {
    filteredContratos() {
      let list = [...this.contratos];
      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        list = list.filter(c =>
          (c.alias || '').toLowerCase().includes(q) ||
          (c.numero_contrato || '').toLowerCase().includes(q) ||
          (c.numero_medidor || '').toLowerCase().includes(q) ||
          (c.proveedor || '').toLowerCase().includes(q)
        );
      }
      if (this.filterServicio !== 'all') {
        list = list.filter(c => c.idServicioPublico === this.filterServicio);
      }
      if (this.filterEstado !== 'all') {
        list = list.filter(c => c.estado === this.filterEstado);
      }
      return list;
    },
    paginatedContratos() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredContratos.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredContratos.length / this.itemsPerPage) || 1;
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    startItem() {
      if (this.filteredContratos.length === 0) return 0;
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredContratos.length);
    }
  },
  mounted() {
    this.cargarTodo();
  },
  watch: {
    searchQuery() { this.currentPage = 1; },
    filterServicio() { this.currentPage = 1; },
    filterEstado() { this.currentPage = 1; }
  },
  methods: {
    getColorServicio,
    getIconoServicio,

    showSuccess(mensaje, titulo = '¡Listo!') {
      useToast().success(`${titulo} — ${mensaje}`, { timeout: 3000, position: 'top-right' });
    },
    showError(mensaje, titulo = 'Error') {
      useToast().error(`${titulo} — ${mensaje}`, { timeout: 4000, position: 'top-right' });
    },
    showWarning(mensaje, titulo = 'Atención') {
      useToast().warning(`${titulo} — ${mensaje}`, { timeout: 3500, position: 'top-right' });
    },

    async cargarTodo() {
      this.loading = true;
      try {
        const [c, s] = await Promise.all([obtenerContratos(), obtenerServiciosPublicos()]);
        if (c.success) this.contratos = c.data || [];
        else this.showError(c.error || 'No se pudieron cargar los contratos');
        if (s.success) this.servicios = (s.data || []).filter(x => x.estado === 'activo');
      } catch (e) {
        console.error(e);
        this.showError('No se pudieron cargar los datos');
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.editing = null;
      this.form = {
        idServicioPublico: 0,
        alias: '',
        numero_contrato: '',
        numero_medidor: '',
        proveedor: '',
        direccion_servicio: '',
        monto_estimado: 0,
        periodicidad: 'mensual',
        dia_vencimiento: 15,
        estado: 'activo'
      };
      this.showModal = true;
    },
    openEditModal(c) {
      this.editing = c;
      this.form = {
        idServicioPublico: c.idServicioPublico,
        alias: c.alias || '',
        numero_contrato: c.numero_contrato || '',
        numero_medidor: c.numero_medidor || '',
        proveedor: c.proveedor || '',
        direccion_servicio: c.direccion_servicio || '',
        monto_estimado: c.monto_estimado || 0,
        periodicidad: c.periodicidad || 'mensual',
        dia_vencimiento: c.dia_vencimiento || 15,
        estado: c.estado || 'activo'
      };
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editing = null;
    },
    async saveContrato() {
      if (!this.form.idServicioPublico) {
        this.showWarning('Debe seleccionar un servicio público');
        return;
      }
      this.saving = true;
      try {
        let res;
        if (this.editing) {
          res = await actualizarContrato(this.editing.idContratoServicio, this.form);
        } else {
          res = await crearContrato(this.form);
        }
        if (res.success) {
          await this.cargarTodo();
          this.closeModal();
          this.showSuccess(this.editing ? 'Contrato actualizado correctamente' : 'Contrato creado correctamente');
        } else {
          this.showError(res.error || 'No se pudo guardar');
        }
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.saving = false;
      }
    },
    abrirModalToggleStatus(c) {
      this.confirmTarget = c;
      this.showConfirmModal = true;
    },
    cancelarConfirmacion() {
      this.showConfirmModal = false;
      this.confirmTarget = null;
      this.confirmLoading = false;
    },
    async ejecutarToggleStatus() {
      if (!this.confirmTarget) return;
      this.confirmLoading = true;
      const esActivo = this.confirmTarget.estado === 'activo';
      try {
        const payload = {
          idServicioPublico: this.confirmTarget.idServicioPublico,
          alias: this.confirmTarget.alias,
          numero_contrato: this.confirmTarget.numero_contrato,
          numero_medidor: this.confirmTarget.numero_medidor,
          proveedor: this.confirmTarget.proveedor,
          direccion_servicio: this.confirmTarget.direccion_servicio,
          monto_estimado: this.confirmTarget.monto_estimado,
          periodicidad: this.confirmTarget.periodicidad,
          dia_vencimiento: this.confirmTarget.dia_vencimiento,
          estado: esActivo ? 'inactivo' : 'activo'
        };
        const res = await actualizarContrato(this.confirmTarget.idContratoServicio, payload);
        if (res.success) {
          await this.cargarTodo();
          this.showSuccess(`Contrato ${esActivo ? 'desactivado' : 'activado'} correctamente`);
        } else {
          this.showError(res.error || 'No se pudo cambiar el estado');
        }
      } catch (e) {
        this.showError(e.message);
      } finally {
        this.confirmLoading = false;
        this.cancelarConfirmacion();
      }
    }
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }
@keyframes fadeInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
</style>